import { useState } from 'react';
import { api, ApiError, type Business } from './api.js';

/**
 * Signing in.
 *
 * One field, and the customer types whatever they have — an email address or a telephone
 * number. Asking them to choose a method first is a decision they have no reason to care about,
 * and the shape of what they typed already answers it.
 *
 * The split in this data is what makes both channels necessary: **97% of these customers have a
 * telephone number and only 71% have an email.** Offering email alone would lock a quarter of
 * them out of their own records.
 */
export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [destination, setDestination] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'ask' | 'code' | 'choose'>('ask');
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const looksLikeEmail = destination.includes('@');
  const digits = destination.replace(/\D/g, '');
  const usable = looksLikeEmail ? /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(destination.trim()) : digits.length >= 10;

  const request = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.requestCode(destination.trim());
      setStage('code');
    } catch {
      setError('Impossible d’envoyer le code pour le moment. Réessayez dans un instant.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.verify(destination.trim(), code.trim());
      // One business is the normal case. Two happens: 11–15% of these customers deal with more
      // than one of the three, and choosing for them would be choosing wrong that often.
      if (result.businesses.length > 1) {
        setBusinesses(result.businesses);
        setStage('choose');
        return;
      }
      onSignedIn();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Ce code ne correspond pas.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <div className="signin__card">
        <Wordmark />

        {stage === 'ask' && (
          <>
            <h1>Vos rendez-vous</h1>
            <p className="muted">
              Entrez le courriel ou le numéro de téléphone que votre entreprise de service a à votre dossier.
              Nous vous enverrons un code.
            </p>
            <label className="field">
              <span>Courriel ou téléphone</span>
              <input
                type="text"
                inputMode={looksLikeEmail ? 'email' : 'tel'}
                autoComplete="username"
                value={destination}
                onChange={(event) => setDestination(event.target.value)}
                placeholder="vous@exemple.ca  ·  450 555-0142"
              />
            </label>
            <button type="button" className="primary" disabled={busy || !usable} onClick={() => void request()}>
              {busy ? 'Un instant…' : 'Recevoir un code'}
            </button>
            <p className="fineprint">
              Le code arrive {looksLikeEmail ? 'par courriel' : 'par message texte'}. Il est valide dix minutes.
            </p>
          </>
        )}

        {stage === 'code' && (
          <>
            <h1>Entrez le code</h1>
            <p className="muted">
              Nous l’avons envoyé à <strong>{destination.trim()}</strong>.
            </p>
            <label className="field">
              <span>Code à six chiffres</span>
              <input
                className="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              />
            </label>
            <button type="button" className="primary" disabled={busy || code.length < 6} onClick={() => void verify()}>
              {busy ? 'Vérification…' : 'Continuer'}
            </button>
            <button type="button" className="ghost" onClick={() => { setStage('ask'); setCode(''); }}>
              Changer d’adresse
            </button>
          </>
        )}

        {stage === 'choose' && (
          <>
            <h1>Quelle entreprise ?</h1>
            <p className="muted">Vous êtes client de plus d’une. Choisissez celle que vous voulez voir.</p>
            <div className="choices">
              {businesses.map((business) => (
                <button
                  key={business.customerId}
                  type="button"
                  className="choice"
                  onClick={async () => {
                    await api.chooseBusiness(business.customerId);
                    onSignedIn();
                  }}
                >
                  <span className="choice__dot" style={{ background: business.brandColor }} aria-hidden="true" />
                  <span>
                    <strong>{business.name}</strong>
                    <span className="muted">{business.displayName}</span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
      </div>
    </main>
  );
}

function Wordmark() {
  return (
    <div className="wordmark" aria-label="Mon Rendez-vous">
      <svg width="34" height="26" viewBox="0 0 64 48" fill="none" aria-hidden="true">
        <path d="M6 12 H28 C38 12 43 9 46 4" stroke="#8e989f" strokeWidth="7" strokeLinecap="round" />
        <path d="M6 26 H34 C44 26 49 23 52 18" stroke="#2c343c" strokeWidth="7" strokeLinecap="round" />
        <path d="M6 40 H40 C50 40 55 37 58 32" stroke="#0e7fa8" strokeWidth="7" strokeLinecap="round" />
      </svg>
      <span>
        monrdv<span className="wordmark__tld">.ca</span>
      </span>
    </div>
  );
}
